import type { ConstructorValue } from '../types';
import { paletteRole, DEFAULT_PALETTE } from '../schema/palette';
import { findKnob, knobDefault, type ComponentTemplate } from '../schema/components';
import { getConstructorBaseline } from '../apply';

/**
 * Resolve a knob value through the cascade (see §4.1 of the spec):
 *
 *   1. overlay.overrides[component][knob]    — Layer 3a session override
 *   2. baseline.overrides[component][knob]   — Layer 3b backend override
 *      (partner's saved theme — shown to the operator as the "current"
 *      value when they open a section for the first time)
 *   3. KnobDef.default                       — Layer 2 component default
 *      (`templateDefaults[template]` first, when the template paints the
 *      element differently)
 *        - if `role`: paletteRole(palette, role)   — Layer 1
 *        - if `literal`: return as-is
 *
 * `value` is the *overlay* (current-session edits). Baseline is read
 * lazily from `apply.ts` so this stays a pure function from the caller's
 * perspective and we don't have to thread baseline through the React
 * tree. Palette resolution merges baseline + overlay so a backend palette
 * role still feeds Layer 1 even before the operator touches the palette.
 *
 * Returns string ready for CSS, or null if component/knob unknown.
 */
export function resolveKnob(
  value: ConstructorValue,
  componentName: string,
  knobName: string,
  template?: ComponentTemplate,
): string | null {
  const baseline = getConstructorBaseline();

  // Layer 3a — session override (operator's current edit)
  const sessionOverride = value.overrides?.[componentName]?.[knobName];
  if (sessionOverride !== undefined) return String(sessionOverride);

  // Layer 3b — backend baseline override (partner's saved theme)
  const baselineOverride = baseline.overrides?.[componentName]?.[knobName];
  if (baselineOverride !== undefined) return String(baselineOverride);

  // Layer 2 — component default
  const knob = findKnob(componentName, knobName);
  if (!knob) return null;

  const def = knobDefault(knob, template);
  if (def.kind === 'literal') return String(def.value);

  // Cascade default — another component's knob feeds this one. Resolve
  // recursively so the placeholder reflects the final colour the user
  // actually sees on the page (matches the CSS fallback chain).
  if (def.kind === 'componentKnob') {
    return resolveKnob(value, def.component, def.knob, template);
  }

  // def.kind === 'role' → Layer 1 palette (overlay > baseline > DEFAULT_PALETTE)
  const mergedPalette = {
    ...DEFAULT_PALETTE,
    ...(baseline.palette ?? {}),
    ...(value.palette ?? {}),
  };
  return paletteRole(mergedPalette, def.role);
}
